import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { GOAL_LABELS, GOAL_TYPES, type GoalType } from "@omina/core";

/**
 * Step 7 — the path she chose, and the one thing it asks her for.
 *
 * Four destinations (7a customers, 7b authority, 7c funding, 7d learning) as
 * one parameterised route rather than four files. The shape is identical every
 * time: a banner carrying the name of the path, one sentence about what Omina
 * still needs, and a single button. What differs is data, which is what
 * `TRACKS` below is — the same rule the option sets follow in curation and
 * interests.
 *
 * Only the customers track has its questions built. The other three say so
 * plainly rather than opening an empty form: an "in progress" that admits it
 * costs a founder five seconds, and a form that collects nothing costs her the
 * five minutes she spent filling it in.
 */
export const Route = createFileRoute("/track/$goal")({
  // A bad goal in the URL is a 404, not a blank screen with undefined in the
  // heading. `GOAL_TYPES` is the same list the picker renders from.
  beforeLoad: ({ params }) => {
    if (!(GOAL_TYPES as readonly string[]).includes(params.goal)) throw notFound();
  },
  component: Track,
});

interface TrackContent {
  /** What the banner says under the path name. */
  kicker: string;
  /** Why Omina is asking, in one sentence. */
  lede: string;
  cta: string;
  /** Absent until that track's questions exist. */
  to?: string;
}

const TRACKS: Record<GoalType, TrackContent> = {
  cust: {
    kicker: "Find the rooms where buyers are",
    lede: "Omina needs to know exactly who buys from you. Answer five questions, or just talk it through, and it builds your ICP blueprint.",
    cta: "Generate your ICP blueprint",
    to: "/blueprint",
  },
  auth: {
    kicker: "Become the name in your category",
    lede: "Omina needs to know what you can speak to, and where your reach already is.",
    cta: "Build your authority profile",
  },
  fund: {
    kicker: "Get in front of the right investors",
    lede: "Omina needs your stage, your round and the kind of investor who backs it.",
    cta: "Build your raise profile",
  },
  learn: {
    kicker: "Rooms that teach you something",
    lede: "Omina needs to know what you are trying to get better at.",
    cta: "Build your learning profile",
  },
};

function Track() {
  const { goal } = Route.useParams();
  const g = goal as GoalType;
  const track = TRACKS[g];

  return (
    <div className="trackwrap">
      {/*
        The banner.

        Drawn rather than photographed: there is no image library in this
        project, and a stock photograph of strangers at a conference is exactly
        the decoration-standing-in-for-information this product avoids. It is
        the brand's own amber-to-coral aura with the path name set on it, which
        is one of the three places that gradient is allowed (see brand.ts). If
        a real illustration ever lands, it drops in behind `.trackbanner` and
        nothing else changes.
      */}
      <div className={`trackbanner track-${g}`}>
        <span className="trackkicker">{track.kicker}</span>
        <h1 className="trackname">{GOAL_LABELS[g].title}</h1>
      </div>

      <p className="lede trackled">{track.lede}</p>

      {track.to ? (
        <Link to={track.to} className="btn trackcta">
          {track.cta}
          <ArrowRight size={15} aria-hidden />
        </Link>
      ) : (
        <>
          {/* Not a disabled button pretending to be nearly ready — it says
              what is true and offers the way that does work today. */}
          <p className="hint trackled">
            These questions are not built yet. Your rooms are still ranked for
            this goal in the meantime.
          </p>
          <Link to="/matches" className="btn trackcta">
            See my rooms
          </Link>
        </>
      )}

      <Link to="/matches" className="authskip">
        Back to my rooms
      </Link>
    </div>
  );
}
